/**
 * Individual assessment tile
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { Chip } from "../../components/ui";
import { styles, btnIconGhost, btnPlain, spin, statusTone } from "./styles";
import type { UploadedAssessment, BusyState, InstrumentDefinition } from "./types";

type Props = {
  item: UploadedAssessment;
  busy: BusyState[string] | undefined;
  onOpen: (id: string) => void;
  onDelete: (item: UploadedAssessment) => void;
  onMarkCompleted?: (item: UploadedAssessment) => void;
  instrument?: InstrumentDefinition;
  uiLang: "en" | "hu";
};

const fmtDate = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const AssessmentTile: React.FC<Props> = ({ item, busy, onOpen, onDelete, onMarkCompleted, instrument, uiLang }) => {
  const { t } = useTranslation();

  if (item.kind === "questionnaire") {
    // Custom (file-based) sends carry their own title in the snapshot name;
    // built-ins resolve via the shared instrument definitions.
    const name = item.instrumentSnapshotName ?? instrument?.name[uiLang] ?? item.instrumentId ?? "?";
    const band = instrument?.scoring.bands.find((b) => b.severity === item.severity);
    const isCompleted = item.status === "completed";
    const isManual = !!item.completedManually;
    const tone = band?.tone ?? item.severityTone;
    const chipTone = !isCompleted
      ? "muted"
      : isManual
      ? "ok"
      : tone === "caution"
      ? "warn"
      : tone === "concern"
      ? "bad"
      : "ok";
    // Custom (raw-sum) sends have no severity band — fall back to a bare
    // score rather than interpolating "undefined" into the chip text.
    const bandLabel = band?.label[uiLang] ?? item.severity ?? null;
    const statusChipText = !isCompleted
      ? t("assessments.statusSent")
      : isManual
      ? t("assessments.statusCompletedManually")
      : bandLabel
      ? `${item.score} · ${bandLabel}`
      : `${item.score}`;
    const channelChipText =
      item.channel === "email" ? t("assessments.channelEmail") : item.channel === "app" ? t("assessments.channelApp") : null;
    const sentLabel = t("assessments.sentAt");
    const completedLabel = t("assessments.completedAt");
    // Mark-completed is Pro-gated AND email-only: patch_questionnaire.py 400s
    // for app-channel sends (they complete through the app flow), so hide it
    // rather than let the therapist hit a doomed click.
    const canMarkCompleted = item.status === "sent" && item.channel === "email";
    // Cancel-send maps to delete_assessment.py's Task 5b guard, which allows
    // deleting ANY still-"sent" questionnaire regardless of channel — so an
    // app-channel send made by mistake stays cancellable here too.
    const canCancel = item.status === "sent";

    return (
      <div style={styles.tile} onClick={() => onOpen(item.id)} role="button">
        <div style={styles.tileHead}>
          <div style={styles.tileTitle}>{name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {channelChipText ? <Chip text={channelChipText} tone="muted" /> : null}
            <Chip text={statusChipText} tone={chipTone} />
          </div>
        </div>
        <div style={styles.tileMeta}>
          {sentLabel}: {item.sentAt ? fmtDate(item.sentAt) : "—"}
          {item.completedAt ? ` · ${completedLabel}: ${fmtDate(item.completedAt)}` : ""}
        </div>
        {canMarkCompleted || canCancel ? (
          <div style={styles.tileActions} onClick={(e) => e.stopPropagation()}>
            {canMarkCompleted ? (
              <button
                style={btnPlain}
                onClick={() => onMarkCompleted?.(item)}
                title={t("assessments.markCompleted")}
                disabled={!!busy}
              >
                <CheckCircle2 size={14} />
                {t("assessments.markCompleted")}
              </button>
            ) : null}
            {canCancel ? (
              <button
                style={btnIconGhost("var(--color-danger)")}
                onClick={() => onDelete(item)}
                title={t("assessments.cancelSend")}
                aria-label={t("assessments.cancelSend")}
                disabled={!!busy}
              >
                <Trash2 size={14} />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  const tone = statusTone(item.parseStatus);
  const label = t(
    item.parseStatus === "ok"
      ? "assessments.statusParsed"
      : item.parseStatus === "pending"
      ? "assessments.statusParsing"
      : item.parseStatus === "error"
      ? "assessments.statusError"
      : "assessments.statusNotParsed"
  );
  const isPending = item.parseStatus === "pending" || busy === "parse";

  return (
    <div style={styles.tile} onClick={() => onOpen(item.id)} role="button">
      <div style={styles.tileHead}>
        <div style={styles.tileTitle} title={item.filename}>
          {item.filename}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isPending ? <Loader2 size={14} style={spin} /> : null}
          <Chip text={label} tone={tone} />
        </div>
      </div>

      <div style={styles.tileMeta}>
        {t("assessments.uploadedAt")}: {fmtDate(item.createdAt)}
        {item.parseModel ? ` · ${t("assessments.parseModel")}: ${item.parseModel}` : ""}
        {item.parsedAt ? ` · ${t("assessments.parsedAt")}: ${fmtDate(item.parsedAt)}` : ""}
      </div>

      <div style={styles.tileActions} onClick={(e) => e.stopPropagation()}>
        <button
          style={btnIconGhost()}
          onClick={() => onOpen(item.id)}
          title={t("assessments.editParser")}
          aria-label={t("assessments.editParser")}
          disabled={!!busy}
        >
          <Pencil size={14} />
        </button>
        <button
          style={btnIconGhost("var(--color-danger)")}
          onClick={() => onDelete(item)}
          title={t("common.delete")}
          aria-label={t("common.delete")}
          disabled={!!busy}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};
